import { formatDateTime } from "@/lib/time";
import type { OnboardingStage } from "@/domain/payment-account/rules";
import type { CapabilityName } from "@/domain/payment-account/types";
import type { LGoBadgeProps } from "@/ui/branded";

export const capabilityLabels: Record<CapabilityName, string> = {
  cardPayments: "Platby kartou",
  transfers: "Převody na váš účet",
  payouts: "Výplaty na bankovní účet",
};

export interface StageCopy {
  badge: string;
  badgeVariant: NonNullable<LGoBadgeProps["variant"]>;
  title: string;
  body: string;
}

export const stageCopy: Record<OnboardingStage, StageCopy> = {
  "no-account": {
    badge: "Není zřízen",
    badgeVariant: "danger",
    title: "Platební účet zatím není zřízen",
    body: "Váš podnik nemá účet u Stripe, přes který by mohl přijímat platby — bez něj nejde prodávat. Účet vám zřídíme a rovnou vás přesměrujeme do ověření ve Stripe.",
  },
  unknown: {
    badge: "Neznámý stav",
    badgeVariant: "neutral",
    title: "Stav účtu se teď nedaří zjistit",
    body: "Se Stripe se nepodařilo spojit a stav účtu jsme zatím nikdy nenačetli. Zkuste to prosím za chvíli znovu.",
  },
  tradeable: {
    badge: "Aktivní",
    badgeVariant: "success",
    title: "Účet je aktivní",
    body: "Stripe ověřil vše potřebné a můžete prodávat. Bankovní údaje a další změny provádíte přímo ve Stripe — portál je nemění.",
  },
  "capability-unavailable": {
    badge: "Chybí oprávnění",
    badgeVariant: "danger",
    title: "Účtu chybí oprávnění, které ověření nedoplní",
    body: "Tuto část účtu Stripe u vašeho účtu vůbec nevede a ověřením ji nezaložíte. Účet je potřeba zřídit znovu se správným nastavením — ozvěte se nám.",
  },
  "action-needed": {
    badge: "Čeká na vás",
    badgeVariant: "gold",
    title: "Stripe čeká na doplnění údajů",
    body: "Dokončete ověření ve Stripe. Formulář můžete kdykoliv opustit a vrátit se sem — nový odkaz vám vytvoříme pokaždé, ten starý už podruhé neplatí.",
  },
  restricted: {
    badge: "Omezeno",
    badgeVariant: "danger",
    title: "Stripe účet omezil",
    body: "Stripe u vašeho účtu omezil část plateb a sám se to neodemkne. Důvod najdete v diagnostice níže a v účtu Stripe — změny se dělají přímo tam, portál je neprovádí.",
  },
  verifying: {
    badge: "Stripe ověřuje",
    badgeVariant: "info",
    title: "Stripe údaje ověřuje",
    body: "Ověření trvá od několika minut po několik pracovních dnů. Nemusíte nic dělat — jakmile Stripe skončí, prodej se odemkne sám.",
  },
};

const rememberedStageCopy: Record<
  Exclude<OnboardingStage, "unknown">,
  Pick<StageCopy, "title" | "body">
> = {
  "no-account": {
    title: "Při posledním načtení nebyl platební účet zřízen",
    body: "Účet u Stripe, přes který by podnik mohl přijímat platby, tehdy neexistoval. Zřídit ho můžete tlačítkem níže.",
  },
  tradeable: {
    title: "Při posledním načtení byl účet aktivní",
    body: "Tehdy měl ověřené vše potřebné a mohl prodávat. Jestli to platí i teď, zjistíte tlačítkem „Načíst stav znovu“.",
  },
  "capability-unavailable": {
    title: "Při posledním načtení účtu chybělo oprávnění",
    body: "Tuto část účtu Stripe tehdy vůbec nevedl a ověřením ji nezaložíte. Ozvěte se nám — účet je potřeba zřídit znovu se správným nastavením.",
  },
  "action-needed": {
    title: "Při posledním načtení Stripe čekal na doplnění údajů",
    body: "Co po vás tehdy chtěl, vidíte níže. Pokud jste ověření mezitím dokončili, načtěte stav znovu.",
  },
  restricted: {
    title: "Při posledním načtení měl Stripe účet omezený",
    body: "Omezení se samo neodemkne — důvod najdete v diagnostice níže a v účtu Stripe, kde se také řeší.",
  },
  verifying: {
    title: "Při posledním načtení Stripe údaje ověřoval",
    body: "Ověření trvá od několika minut po několik pracovních dnů. Nemusíte nic dělat — jakmile Stripe skončí, prodej se odemkne sám.",
  },
};

/** A stage the last Stripe read failed to refresh has to read as a single
 *  remembered claim: a partner mid-refund must not take „můžete prodávat“ from
 *  an hour ago for a live answer. The stage itself stays visible — what was
 *  true an hour ago beats „nevíme“ — and nothing here says the account cannot
 *  sell, because what happened is that we could not ask.
 *  `unknown` has nothing remembered to show: it means no read ever succeeded. */
export function rememberedCopy(stage: OnboardingStage, lastReadAt: Date | null): StageCopy | null {
  if (stage === "unknown" || lastReadAt === null) return null;
  const remembered = rememberedStageCopy[stage];
  return {
    badge: `Naposledy: ${stageCopy[stage].badge}`,
    badgeVariant: "neutral",
    title: remembered.title,
    body: `Se Stripe se teď nedaří spojit, takže vidíte stav z ${formatDateTime(lastReadAt)}. ${remembered.body}`,
  };
}

/** Said when the read itself failed, not when Stripe answered badly: nothing
 *  about the account is known, and the sentence must say so, because the
 *  card for „účet neexistuje“ is what a partner would otherwise assume. */
export const unavailableCopy: StageCopy = {
  badge: "Nedostupné",
  badgeVariant: "neutral",
  title: "Stav platebního účtu se teď nepodařilo načíst",
  body: "Portál se k údajům o vašem účtu nedostal. Neznamená to, že účet neexistuje — jen se na něj teď nedalo zeptat. Zkuste to prosím znovu.",
};
